// PHA-1168: the saved reports visible to an org, for the Reports page and the
// Ask Phanova `#` picker. One fetch per org is shared (and cached briefly) so
// opening the chat, typing `#` and visiting /reports don't each hit the API.
import { useCallback, useEffect, useState } from 'react';
import { customViewsApi, ReportSummary } from '../../services/api';

export interface SavedReportsData {
  reports: ReportSummary[];
  canDesign: boolean;
}

const EMPTY: SavedReportsData = { reports: [], canDesign: false };
const DEFAULT_MAX_AGE_MS = 60_000;

const cache = new Map<string, { at: number; data: SavedReportsData }>();
const inflight = new Map<string, Promise<SavedReportsData>>();

export const resetSavedReportsCache = () => {
  cache.clear();
  inflight.clear();
};

/** Drop an org's cached list, e.g. after a report is saved or archived. */
export const invalidateSavedReports = (orgId?: string) => {
  if (orgId) {
    cache.delete(orgId);
    inflight.delete(orgId);
  } else {
    cache.clear();
    inflight.clear();
  }
};

const fetchReports = (orgId: string): Promise<SavedReportsData> => {
  const existing = inflight.get(orgId);
  if (existing) return existing;
  // A promise chain (not an async body) so a synchronous failure still settles
  // after inflight.set below, and never leaves a stale entry behind.
  const request: Promise<SavedReportsData> = Promise.resolve()
    .then(() => customViewsApi.list(orgId, undefined, 'report'))
    .then((res) => {
      const data = { reports: (res.views || []) as ReportSummary[], canDesign: !!res.can_design };
      cache.set(orgId, { at: Date.now(), data });
      return data;
    })
    .catch((err) => {
      // Reports are an extra: an outage or a clinic without them is just "none".
      console.warn('Saved reports unavailable:', err);
      return EMPTY;
    })
    .finally(() => {
      if (inflight.get(orgId) === request) inflight.delete(orgId);
    });
  inflight.set(orgId, request);
  return request;
};

interface Options {
  /** Hold the fetch until the data is actually needed (e.g. the user typed `#`). */
  enabled?: boolean;
  /** Reuse a cached list younger than this; 0 always refetches. */
  maxAgeMs?: number;
}

export function useSavedReports(orgId: string, { enabled = true, maxAgeMs = DEFAULT_MAX_AGE_MS }: Options = {}) {
  const [data, setData] = useState<SavedReportsData>(() => cache.get(orgId)?.data ?? EMPTY);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => {
    invalidateSavedReports(orgId);
    setTick((t) => t + 1);
  }, [orgId]);

  useEffect(() => {
    if (!enabled || !orgId) {
      setData(EMPTY);
      return;
    }
    const cached = cache.get(orgId);
    if (cached && Date.now() - cached.at < maxAgeMs) {
      setData(cached.data);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchReports(orgId).then((next) => {
      if (cancelled) return;
      setData(next);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [orgId, enabled, maxAgeMs, tick]);

  return { ...data, loading, refresh };
}
